/**
 * İÇERİK STÜDYOSU — tüm içerik yolculuğu TEK pipeline görünümünde:
 * keşfedildi → derinleştirildi → onaylandı → üretildi → yayınlandı (yakında).
 * Mevcut sayfalar (Keşif Ajanı, brief detay, Üretim Ajanı, içerik sayfası) kalır;
 * bu sayfa hepsinin üstünde gezinme katmanıdır. + Fırsat Havuzu sekmesi:
 * dağınık "fırsat" kavramı (planner tablosu + derin analiz kelime fırsatları) tek yerde.
 */
import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { ContentScorecard } from "@/components/content-scorecard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Globe, ArrowRight } from "lucide-react";

const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));

type PBrief = {
  title: string; targetKeyword: string; volume: number; clusterName: string | null;
  intentKind?: string; priority: string; approved?: boolean;
  deepDive?: { generatedAt: string; keywordOpportunities: { keyword: string; volume: number; difficulty: number }[] } | null;
};
type PlannerResp = {
  data: { briefs: PBrief[]; opportunityKeywords?: { keyword: string; volume: number; difficulty: number; cpc?: number; source: string; usedInBrief: boolean }[]; meta: { market: string } } | null;
  generatedAt: string | null;
};
type WRow = { id: number; status: string; running?: boolean; brief: { title: string; targetKeyword: string; volume: number }; result: { title: string; wordCount: number; audit: { score: number } | null } | null };

const COLS = [
  { key: "kesif", title: "🔍 Keşfedildi", hint: "Keşif Ajanı önerdi — derin analiz bekliyor", color: "#70706c" },
  { key: "derin", title: "🔬 Derinleştirildi", hint: "14 bölümlü analiz hazır — onay bekliyor", color: "#ec6461" },
  { key: "onay", title: "✅ Onaylandı", hint: "Editör onayladı / üretim havuzunda", color: "#f79009" },
  { key: "uretildi", title: "✍️ Üretildi", hint: "İçerik hazır — yayına alınabilir", color: "#12b76a" },
  { key: "yayin", title: "🚀 Yayınlandı · İzleniyor", hint: "Yakında: yayın doğrulama + prompt takibi", color: "#e83e3c" },
] as const;

export default function ContentStudio() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const [tab, setTab] = useState<"pipeline" | "havuz">("pipeline");

  const { data: planner } = useQuery({
    queryKey: ["content-planner", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<PlannerResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/content-planner`); return r.ok ? await r.json() : { data: null, generatedAt: null }; } catch { return { data: null, generatedAt: null }; }
    },
  });
  const { data: writerData } = useQuery({
    queryKey: ["writer-briefs", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<{ briefs: WRow[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs`); return r.ok ? await r.json() : { briefs: [] }; } catch { return { briefs: [] }; }
    },
  });

  const pBriefs = planner?.data?.briefs ?? [];
  const wRows = writerData?.briefs ?? [];
  const wByKw = new Map(wRows.map((w) => [w.brief.targetKeyword, w]));

  // Kart durumu: üretildi > onaylandı (veya havuzda) > derinleştirildi > keşfedildi
  type CardT = { kw: string; title: string; volume: number; cluster: string | null; col: string; writerId?: number; auditScore?: number | null; deepDive: boolean; approved: boolean };
  const cards: CardT[] = [];
  const seen = new Set<string>();
  for (const b of pBriefs) {
    const w = wByKw.get(b.targetKeyword);
    const col = w?.status === "yazildi" ? "uretildi" : (b.approved || w) ? "onay" : b.deepDive ? "derin" : "kesif";
    cards.push({ kw: b.targetKeyword, title: b.title, volume: b.volume, cluster: b.clusterName, col, writerId: w?.id, auditScore: w?.result?.audit?.score ?? null, deepDive: !!b.deepDive, approved: !!b.approved });
    seen.add(b.targetKeyword);
  }
  // Yalnız üretim havuzunda olan (eski koşulardan) brief'ler
  for (const w of wRows) {
    if (seen.has(w.brief.targetKeyword)) continue;
    cards.push({ kw: w.brief.targetKeyword, title: w.result?.title ?? w.brief.title, volume: w.brief.volume, cluster: null, col: w.status === "yazildi" ? "uretildi" : "onay", writerId: w.id, auditScore: w.result?.audit?.score ?? null, deepDive: false, approved: true });
  }

  // ── FIRSAT HAVUZU: planner fırsat tablosu + derin analiz kelime fırsatları TEK listede ──
  type Opp = { keyword: string; volume: number; difficulty: number; source: string; usedInBrief: boolean };
  const oppMap = new Map<string, Opp>();
  for (const o of planner?.data?.opportunityKeywords ?? []) {
    oppMap.set(o.keyword.toLowerCase(), { keyword: o.keyword, volume: o.volume, difficulty: o.difficulty, source: o.source, usedInBrief: o.usedInBrief });
  }
  for (const b of pBriefs) {
    for (const k of b.deepDive?.keywordOpportunities ?? []) {
      const key = k.keyword.toLowerCase();
      if (!oppMap.has(key)) oppMap.set(key, { keyword: k.keyword, volume: k.volume, difficulty: k.difficulty, source: `derin analiz (${b.targetKeyword})`, usedInBrief: false });
    }
  }
  const briefKws = new Set(pBriefs.map((b) => b.targetKeyword.toLowerCase()));
  const opps = [...oppMap.values()]
    .map((o) => ({ ...o, usedInBrief: o.usedInBrief || briefKws.has(o.keyword.toLowerCase()) }))
    .sort((a, b) => b.volume - a.volume);

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0" style={{ background: "rgba(139,92,246,0.12)" }}>🎬</div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">İçerik Stüdyosu</h1>
            <p className="text-sm text-muted-foreground">Tüm içerik yolculuğu tek hatta: keşif → analiz → onay → üretim → yayın</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b">
        {([["pipeline", "🎬 Pipeline"], ["havuz", `💎 Fırsat Havuzu (${opps.length})`]] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className="px-4 py-2 text-sm font-medium border-b-2 transition"
            style={tab === k ? { borderColor: "#e83e3c", color: "#d42b29" } : { borderColor: "transparent", color: "#70706c" }}>{l}</button>
        ))}
      </div>

      {tab === "pipeline" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {COLS.map((col) => {
            const colCards = cards.filter((c) => c.col === col.key);
            return (
              <div key={col.key} className="rounded-xl border p-3" style={{ borderColor: `${col.color}44`, background: `${col.color}08` }}>
                <div className="text-sm font-semibold mb-0.5" style={{ color: col.color }}>{col.title} <span className="text-xs font-normal">({colCards.length})</span></div>
                <div className="text-[10px] text-muted-foreground mb-3">{col.hint}</div>
                <div className="space-y-2">
                  {colCards.map((c) => (
                    <Card key={c.kw}><CardContent className="p-2.5 text-xs">
                      <div className="font-semibold leading-snug">{c.title}</div>
                      <div className="text-muted-foreground mt-1">{c.kw}{c.volume > 0 ? ` · ${fmt(c.volume)}/ay` : ""}</div>
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {c.deepDive && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#ec646118", color: "#b12220" }}>🔬 analiz</span>}
                        {c.approved && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#12b76a18", color: "#039855" }}>✅ onaylı</span>}
                        {typeof c.auditScore === "number" && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>🛡️ {c.auditScore}</span>}
                      </div>
                      <div className="mt-1.5">
                        {c.col === "uretildi" && c.writerId
                          ? <Link href={`/content/${c.writerId}`}><span className="hover:underline cursor-pointer inline-flex items-center gap-0.5 text-brand-600 dark:text-brand-400">İçeriği aç <ArrowRight className="w-3 h-3" /></span></Link>
                          : c.col === "onay"
                            ? <Link href="/content-opportunities"><span className="hover:underline cursor-pointer inline-flex items-center gap-0.5 text-brand-600 dark:text-brand-400">Üretim Ajanı <ArrowRight className="w-3 h-3" /></span></Link>
                            : <Link href={`/planner-brief/${encodeURIComponent(c.kw)}`}><span className="hover:underline cursor-pointer inline-flex items-center gap-0.5 text-brand-600 dark:text-brand-400">Brief detayı <ArrowRight className="w-3 h-3" /></span></Link>}
                      </div>
                    </CardContent></Card>
                  ))}
                  {colCards.length === 0 && <div className="text-[10px] text-muted-foreground text-center py-4">{col.key === "yayin" ? "Yakında: yayın URL'i girip doğrulama + GEO prompt takibi" : "boş"}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "havuz" && (
        <Card><CardContent className="p-0">
          {opps.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Fırsat verisi yok — önce <Link href="/content-planner"><span className="underline cursor-pointer">Brief Keşif Ajanı</span></Link>'nı çalıştır. Arama verisi tabanlı fırsatlar için <Link href="/traffic-analysis"><span className="underline cursor-pointer">Trafik Analizi → Fırsat Motoru</span></Link>'na bak.</div>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="border-b text-muted-foreground">
                <th className="text-left font-medium px-4 py-2">Kelime</th>
                <th className="text-right font-medium px-3 py-2">Hacim</th>
                <th className="text-right font-medium px-3 py-2">KD</th>
                <th className="text-left font-medium px-3 py-2">Kaynak</th>
                <th className="text-left font-medium px-4 py-2">Durum</th>
              </tr></thead>
              <tbody>
                {opps.slice(0, 100).map((o) => (
                  <tr key={o.keyword} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-1.5 font-medium">{o.keyword}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{o.volume > 0 ? `${fmt(o.volume)}/ay` : "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{o.difficulty || "—"}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{o.source}</td>
                    <td className="px-4 py-1.5">
                      {o.usedInBrief
                        ? <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: "#12b76a15", color: "#039855" }}>✅ brief'e dönüştü</span>
                        : <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>bekliyor</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>
      )}

      {activeDomainId != null && <ContentScorecard domainId={activeDomainId} />}

      <div className="mt-4 text-[11px] text-muted-foreground">
        Hızlı erişim: <Link href="/content-planner"><Button size="sm" variant="ghost" className="text-[11px] h-6 px-2">🔍 Brief Keşif Ajanı</Button></Link>
        <Link href="/content-opportunities"><Button size="sm" variant="ghost" className="text-[11px] h-6 px-2">🤖 İçerik Üretim Ajanı</Button></Link>
        <Link href="/planning"><Button size="sm" variant="ghost" className="text-[11px] h-6 px-2">📅 Planlama</Button></Link>
      </div>
    </div>
  );
}
